// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { Alert, Checkbox, Modal } from "antd";

// What the leave flow needs to warn about / offer for a given room.
export interface LeavePrompt {
  roomID: string;
  name: string;
  isSpace: boolean;
  isLastOperator: boolean; // you're the only operator — the room will be unmoderated
  inviteOnly: boolean; // you may not be able to rejoin
  joinedChildren: string[]; // child channels you're in (Spaces only)
}

// LeaveDialog confirms a leave when there's something to warn about (last operator,
// invite-only lockout) or to offer (cascade-leave a Space's channels). Frictionless
// leaves (a plain channel with other operators) skip it entirely.
export function LeaveDialog({
  prompt,
  onCancel,
  onConfirm,
}: {
  readonly prompt: LeavePrompt | null;
  readonly onCancel: () => void;
  readonly onConfirm: (alsoLeaveChildren: string[]) => void;
}) {
  const [alsoChildren, setAlsoChildren] = useState(true);
  useEffect(() => {
    if (prompt) setAlsoChildren(true);
  }, [prompt]);

  const childCount = prompt?.joinedChildren.length ?? 0;

  return (
    <Modal
      title={prompt ? `Leave ${prompt.name}?` : ""}
      open={!!prompt}
      onCancel={onCancel}
      onOk={() => prompt && onConfirm(alsoChildren ? prompt.joinedChildren : [])}
      okText="Leave"
      okButtonProps={{ danger: true }}
    >
      {prompt?.isLastOperator && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="You're the only operator"
          description="No moderators will remain — topic, modes, and kicks/bans will be frozen. Consider promoting another member (Make Founder) before you go."
        />
      )}
      {prompt?.inviteOnly && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This room is invite-only"
          description="You may not be able to rejoin without a fresh invite."
        />
      )}
      {prompt?.isSpace && childCount > 0 && (
        <Checkbox checked={alsoChildren} onChange={(e) => setAlsoChildren(e.target.checked)}>
          Also leave the {childCount} channel{childCount === 1 ? "" : "s"} you joined in this space
        </Checkbox>
      )}
    </Modal>
  );
}
